import React, { useEffect, useState } from 'react';
import { Box, Typography, Card, CardActionArea, CardContent, Chip, CircularProgress, Alert, Stack } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/common/PageLayout';
import { get } from '../utils/api';

export default function SpecialSponsorshipsPage() {
  const navigate = useNavigate();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await get('/api/manage/special-sponsorships/programs', {}, { cache: 'no-store' });
        if (!cancelled) setPrograms(Array.isArray(res?.programs) ? res.programs : []);
      } catch (e) { if (!cancelled) setError(e?.message || 'Failed to load programs'); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <PageLayout contentMaxWidth="lg" showHeaderImage={false} showOrgHeader>
      <Box sx={{ mb: 3 }}>
        <Typography variant="overline" color="text.secondary">Manage · Special sponsorships</Typography>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Lifetime sponsorships</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          One-of-a-kind sponsorships with lifetime recognition. Track donors and send recognition emails tied to each occasion.
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          {programs.map((p) => (
            <Card key={p.slug} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, '&:hover': { borderColor: 'primary.main', boxShadow: 2 } }}>
              <CardActionArea onClick={() => navigate(`/manage/special-sponsorships/${encodeURIComponent(p.slug)}`)}>
                <CardContent sx={{ py: 2.25 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{p.name}</Typography>
                    {p.active ? <Chip size="small" color="success" label="Active" /> : <Chip size="small" label="Inactive" />}
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{p.description}</Typography>
                  <Stack direction="row" spacing={3} alignItems="center">
                    <Typography variant="body2"><b>{p.item_count}</b> <span style={{ color: 'var(--mui-palette-text-secondary)' }}>vahanas</span></Typography>
                    <Typography variant="body2"><b>{p.donor_count}</b> sponsors</Typography>
                    <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', color: 'primary.main' }}>Open <ChevronRightIcon fontSize="small" /></Box>
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
          {programs.length === 0 && <Typography color="text.secondary">No programs yet.</Typography>}
        </Box>
      )}
    </PageLayout>
  );
}
